import { useState, useEffect, useRef } from 'react';
import { Document, Page, Text, View, StyleSheet, pdf, Image } from '@react-pdf/renderer';
import QRCode from 'qrcode';
import { API_URL } from '../../../config/api';

// PDF Styles matching the legacy worksheet
const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 12,
    fontFamily: 'Helvetica',
  },
  header: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  section: {
    marginBottom: 6,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  label: {
    width: '25%',
    fontWeight: 'normal',
    fontSize: 12,
  },
  value: {
    width: '25%',
    fontWeight: 'bold',
    fontSize: 12,
  },
  sectionHeader: {
    fontSize: 13,
    fontWeight: 'bold',
    marginTop: 6,
    marginBottom: 3,
  },
  table: {
    border: '1px solid #a0a0a0',
    marginBottom: 5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #e0e0e0',
    padding: 3,
  },
  tableCell: {
    width: '25%',
    padding: 2,
    fontSize: 12,
  },
  tableCellLabel: {
    width: '25%',
    padding: 2,
    fontSize: 12,
  },
  tableCellValue: {
    width: '25%',
    padding: 2,
    fontWeight: 'bold',
    fontSize: 12,
  },
  roomTasksTable: {
    border: '1px solid #000',
    marginTop: 3,
    marginBottom: 5,
  },
  roomTasksHeader: {
    flexDirection: 'row',
    backgroundColor: '#1C5E55',
    color: 'white',
    padding: 3,
    fontWeight: 'bold',
    fontSize: 11,
  },
  roomTasksRow: {
    flexDirection: 'row',
    backgroundColor: '#E3EAEB',
    borderBottom: '1px solid #ccc',
    padding: 2,
  },
  roomTasksCell: {
    padding: 1,
    fontSize: 10,
  },
  checkbox: {
    width: 12,
    height: 12,
    border: '1px solid #000',
  },
  commentsSection: {
    marginTop: 5,
  },
  commentsBox: {
    border: '1px solid #000',
    minHeight: 35,
    padding: 3,
    marginTop: 3,
    fontSize: 12,
  },
  barcodeSection: {
    marginTop: 5,
    alignItems: 'center',
    border: '1px dashed #000',
    padding: 5,
  },
  qrCode: {
    width: 60,
    height: 60,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 5,
  },
  barcodeText: {
    fontSize: 11,
    textAlign: 'center',
    marginTop: 2,
  },
});

// PDF Document Component
const WorksheetPDF = ({ data, qrCodeDataUrl }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.section}>
        <Text style={styles.header}>Order Work Sheet</Text>
      </View>

      {/* Basic Information */}
      <View style={styles.section}>
        <View style={styles.row}>
          <Text style={styles.label}>Work Order Number:</Text>
          <Text style={styles.value}>{data.orderDetails?.orderNo || 'N/A'}</Text>
          <Text style={styles.label}>Head Dimensions:</Text>
          <Text style={styles.value}>
            Hairline to Crown: {data.orderDetails?.hairlineToCrown || 'N/A'}, Hairline to Nape: {data.orderDetails?.hairlineToNape || 'N/A'}, Ear to Ear: {data.orderDetails?.earToEar || 'N/A'}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Work Order Date:</Text>
          <Text style={styles.value}>
            {data.orderDetails?.orderDate ? new Date(data.orderDetails.orderDate).toLocaleString() : 'N/A'}
          </Text>
          <Text style={styles.label}>Customer Phone:</Text>
          <Text style={styles.value}>{data.orderDetails?.orderPhone || 'N/A'}</Text>
        </View>
        {/* <View style={styles.row}>
          <Text style={styles.label}>From Warehouse:</Text>
          <Text style={styles.value}>Pomona</Text>
        </View> */}
        <View style={styles.row}>
          <Text style={styles.label}>Order Due Date:</Text>
          <Text style={styles.value}>
            {data.orderDetails?.dueDate ? new Date(data.orderDetails.dueDate).toLocaleDateString() : 'N/A'}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Customer Name:</Text>
          <Text style={styles.value}>{data.customerInfo?.customerName || data.orderDetails?.orderCustomerName || 'N/A'}</Text>
          <Text style={styles.label}>Order Type:</Text>
          <Text style={styles.value}>{data.orderDetails?.orderType || 'N/A'}</Text>
        </View>
      </View>

      {/* Wig Build Details */}
      <View style={styles.section}>
        <Text style={styles.sectionHeader}>Wig Build Details:</Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Base Color:</Text>
            <Text style={styles.tableCellValue}>{data.wigDetails?.baseColor || 'N/A'}</Text>
            <Text style={styles.tableCellLabel}>Top Size:</Text>
            <Text style={styles.tableCellValue}>{data.wigDetails?.topSize || 'N/A'}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Wig Type:</Text>
            <Text style={styles.tableCellValue}>{data.wigDetails?.wigType || 'N/A'}</Text>
            <Text style={styles.tableCellLabel}>Wig Length:</Text>
            <Text style={styles.tableCellValue}>{data.wigDetails?.wigLength || 'N/A'}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Net Size:</Text>
            <Text style={styles.tableCellValue}>{data.wigDetails?.netSize || 'N/A'}</Text>
            <Text style={styles.tableCellLabel}>Hair Type:</Text>
            <Text style={styles.tableCellValue}>{data.wigDetails?.hairType || 'N/A'}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Net Type:</Text>
            <Text style={styles.tableCellValue}>{data.wigDetails?.netType || 'N/A'}</Text>
            <Text style={styles.tableCellLabel}>Lace Type:</Text>
            <Text style={styles.tableCellValue}>{data.wigDetails?.laceType || 'N/A'}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Serial Code:</Text>
            <Text style={styles.tableCellValue}>{data.wigDetails?.wigBarCode || data.orderDetails?.wigBarCode || 'N/A'}</Text>
            <Text style={styles.tableCellLabel}>Mold ID:</Text>
            <Text style={styles.tableCellValue}>{data.customerInfo?.moldId || 'N/A'}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Wig Weight (g):</Text>
            <Text style={styles.tableCellValue}>{data.wigDetails?.wigWeight || '0'}</Text>
            <Text style={styles.tableCellLabel}>Order Comments:</Text>
            <Text style={styles.tableCellValue}>{data.orderDetails?.Custom_Comments || 'N/A'}</Text>
          </View>
        </View>
      </View>

      {/* Room Routing and Work Order Tasks */}
      <View style={styles.section}>
        <Text style={styles.sectionHeader}>Room Routing and Work Order Tasks:</Text>
        <View style={styles.roomTasksTable}>
          <View style={styles.roomTasksHeader}>
            <Text style={[styles.roomTasksCell, { width: '7%' }]}>Room</Text>
            {/* <Text style={[styles.roomTasksCell, { width: '10%' }]}>Name</Text> */}
            <Text style={[styles.roomTasksCell, { width: '25%' }]}>Task</Text>
            <Text style={[styles.roomTasksCell, { width: '53%' }]}>Notes</Text>
            <Text style={[styles.roomTasksCell, { width: '25%' }]}>QC Notes</Text>
            {/* <Text style={[styles.roomTasksCell, { width: '10%' }]}>Worker</Text> */}
          </View>
          {data.roomTasks?.map((task, index) => (
            <View key={index} style={styles.roomTasksRow}>
              
              <Text style={[styles.roomTasksCell, { width: '7%' }]}>{task.RoomNo || 'N/A'}</Text>
              {/* <Text style={[styles.roomTasksCell, { width: '10%' }]}>{task.RoomDescription || 'N/A'}</Text> */}
              <Text style={[styles.roomTasksCell, { width: '25%' }]}>{task.RoomTask || 'N/A'}</Text>
              <Text style={[styles.roomTasksCell, { width: '53%' }]}>{task.TaskNotes || '-'}</Text>
              <Text style={[styles.roomTasksCell, { width: '25%' }]}>{task.QC_Notes || '-'}</Text>
              {/* <Text style={[styles.roomTasksCell, { width: '10%' }]}>{task.AssignedWorker || '-'}</Text> */}
            </View>
          ))}
        </View>
      </View>

      {/* Footer with Comments and Barcode */}
      <View style={styles.footer}>
        <View style={{ width: '60%' }}>
          <Text style={styles.sectionHeader}>Comments or Special Instructions:</Text>
          <View style={styles.commentsBox}>
            <Text>-</Text>
          </View>
        </View>
        <View style={{ width: '35%' }}>
          <View style={styles.barcodeSection}>
            <Text style={styles.barcodeText}>{data.customerInfo?.customerName || 'N/A'} {data.wigDetails?.wigSerialCode || ''}</Text>
            {qrCodeDataUrl && <Image src={qrCodeDataUrl} style={styles.qrCode} />}
            <Text style={styles.barcodeText}>{data.wigDetails?.wigBarCode || 'N/A'}</Text>
          </View>
          <Text style={[styles.barcodeText, { marginTop: 3 }]}>Order No: {data.orderDetails?.orderNo || 'N/A'}</Text>
        </View>
      </View>
    </Page>
  </Document>
);

// Main Component
const StockWigWorksheet = ({ orderNo, onClose }) => {
  const [worksheetData, setWorksheetData] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printTriggered, setPrintTriggered] = useState(false);
  const hasOpenedRef = useRef(false);
  const iframeRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => {
    if (orderNo && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      fetchWorksheetData();
    }

    // Cleanup function - remove iframe when component unmounts
    return () => {
      if (iframeRef.current) {
        try {
          document.body.removeChild(iframeRef.current);
        } catch (e) {
          console.error('Error removing iframe:', e);
        }
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
    };
  }, [orderNo]);

  const fetchWorksheetData = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/api/rachels-room/worksheet/${orderNo}`);

      if (!response.ok) {
        throw new Error('Failed to fetch worksheet data');
      }

      const data = await response.json();
      setWorksheetData(data);

      // Generate QR Code
      let qrUrl = null;
      if (data.wigDetails?.wigBarCode) {
        qrUrl = await QRCode.toDataURL(data.wigDetails.wigBarCode, {
          width: 60,
          margin: 1,
        });
        setQrCodeDataUrl(qrUrl);
      }

      setLoading(false);

      // Automatically open PDF
      await openPDF(data, qrUrl);
    } catch (err) {
      console.error('Error fetching worksheet data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const openPDF = async (data, qrUrl) => {
    try {
      const doc = <WorksheetPDF data={data} qrCodeDataUrl={qrUrl || qrCodeDataUrl} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);

      // Store URL for cleanup
      urlRef.current = url;

      // Create hidden iframe to trigger print dialog in same tab
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);

      // Store iframe reference for cleanup
      iframeRef.current = iframe;

      // Wait for iframe to load, then trigger print
      iframe.onload = function() {
        // Add a small delay to ensure PDF is fully loaded
        setTimeout(() => {
          try {
            setPrintTriggered(true);
            iframe.contentWindow.print();

            // IMPORTANT: Don't remove iframe - it needs to stay alive for print dialog
            // It will be cleaned up when modal is closed

          } catch (printError) {
            console.error('Error triggering print:', printError);
            // Fallback: open in new window if iframe print fails
            window.open(url, '_blank');
            // Clean up immediately on error
            try {
              document.body.removeChild(iframe);
            } catch (removeError) {
              console.error('Error removing iframe:', removeError);
            }
            URL.revokeObjectURL(url);
            if (onClose) {
              onClose();
            }
          }
        }, 500);
      };

      // Handle iframe load errors
      iframe.onerror = function(error) {
        console.error('Error loading PDF in iframe:', error);
        setError('Failed to load PDF');
        URL.revokeObjectURL(url);
        if (onClose) {
          onClose();
        }
      };
    } catch (error) {
      console.error('Error opening PDF:', error);
      setError(`Failed to generate PDF: ${error.message}`);
      if (onClose) {
        onClose();
      }
    }
  };

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: 'red', marginBottom: '20px' }}>Error: {error}</p>
        <button
          onClick={onClose}
          style={{
            padding: '10px 20px',
            backgroundColor: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Close
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Preparing worksheet for printing...</p>
      </div>
    );
  }

  // Show message with close button after print is triggered
  return (
    <div
      onClick={onClose}
      style={{
        padding: '40px',
        textAlign: 'center',
        cursor: 'pointer',
        userSelect: 'none'
      }}
    >
      <p style={{ marginBottom: '15px', fontSize: '16px' }}>
        {printTriggered ? '✓ Print dialog opened' : 'Click anywhere to close'}
      </p>
      <p style={{ fontSize: '14px', color: '#666' }}>
        (Click anywhere on this message to close)
      </p>
    </div>
  );
};

export default StockWigWorksheet;
